import { useState, useEffect } from "react";
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import lodash from "lodash";
import deepdash from "deepdash";
import { lang } from "config";
import Input from "components/forms/input";
import nProgress from "nprogress";
import toast from "react-hot-toast";
import { useResultSet } from "repositories/db-repository";
import { useAuth } from "contexts/auth-context";
import { getIsAdmin, getIsSupervisor, getIsOwner, getParameter, getGenericSupplier, getBranchList } from "helpers/general-helper";
import SelectSearchable from "components/forms/select-searchable";
import ErrorMessage from "components/forms/error-message";
import AddProduct from "../purchase/add-product"
import AsyncPOHeaderPicker from "./AsyncPOHeaderPicker";

/**
 * UI Form Return (BY_PO atau BY_PRODUCT) – gaya & struktur mengikuti Form PO kamu.
 * Props:
 *  - data: {
 *      return_header: [...],                // opsional: header yang sudah di-load
 *      return_item: [...],                  // opsional: item return yang sudah di-load
 *      return_item_side_by_side: [...],     // opsional: versi side-by-side (kalau kamu punya aggregator spt PO)
 *      return_header_side_by_side: {...}    // opsional: aggregasi nilai (amount/dll)
 *      purchase_header: [...],              // penting kalau user pilih BY_PO (mengambil supplier/warehouse)
 *      purchase_item_side_by_side: [...]    // untuk bantu pilih item saat BY_PO (opsional)
 *    }
 *  - errors: { ... }                        // error map untuk form
 *  - onSubmit: (payload) => void            // handler pengiriman state -> API mu (sama spt PO)
 */
export default function ReturnForm({ data, errors, onSubmit }) {
  const _ = deepdash(lodash);

  const { can, user, isSuper, currentBranch, setCurrentBranch } = useAuth();
  const { setTitle, title, transition, setTransition } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams] = useSearchParams();
  const search = searchParams.get("search");

  // selection master
  const branchSelection = getBranchList();
  const supplierSelection = getGenericSupplier();

  // ambil produk dari Inventory (sama seperti PO contohmu)
  const outlet_group_id_default = getParameter("OUTLET_GROUP_ID");
  const remax_hq_outlet_id = getParameter("REMAX_HQ_OUTLET_ID");

    const { data: psListRes } = useResultSet(
    `${process.env.REACT_APP_INVENTORY_API_URL}`,
    `ps_list`,
    { outlet_group_id: outlet_group_id_default, outlet_id: remax_hq_outlet_id }
    );

    // Safe array: kalau psListRes itu array → pakai langsung,
    // kalau object dg {data:[...]} → pakai data-nya, selain itu → []
    const productUnitOptions = Array.isArray(psListRes)
    ? psListRes
    : Array.isArray(psListRes?.data)
    ? psListRes.data
    : [];


  // =========================================================
  // STATE UTAMA (HEADER)
  // =========================================================
  const origin = "RT"; // Return

  // pakai header pertama jika sudah ada (misal ketika edit)
  const selectedReturnHeader = data?.data[0] ?? [];
  console.log("selectedReturnHeader", selectedReturnHeader);

  const [id, set_id] = useState(selectedReturnHeader?.id ?? null);
  const [outlet_group_id, set_outlet_group_id] = useState(selectedReturnHeader?.outlet_group_id ?? outlet_group_id_default ?? null);
  const [outlet_id, set_outlet_id] = useState(selectedReturnHeader?.outlet_id ?? selectedReturnHeader?.outlet_group_id ?? null);

  const [outlet_group_name, set_outlet_group_name] = useState(getParameter("OUTLET_GROUP_NAME") ?? null);
  const [outlet_group_phone_number, set_outlet_group_phone_number] = useState(getParameter("OUTLET_GROUP_PHONE_NUMBER") ?? null);

  const [user_id, set_user_id] = useState(user?.id ?? null);
  const [user_name, set_user_name] = useState(user?.name ?? null);

  const [outlet_name, set_outlet_name] = useState(currentBranch?.name ?? null);
  const [outlet_phone_number, set_outlet_phone_number] = useState(currentBranch?.contact ?? null);
  const [outlet_email_address, set_outlet_email_address] = useState(null);

  const [entry_date, set_entry_date] = useState(selectedReturnHeader?.created_at ?? null);

  // flow status untuk Return
  const [flow_detail_id, set_flow_detail_id] = useState(selectedReturnHeader?.flow_detail_id ?? "RETURN_DRAFT");
  const [latest_flow_detail_id, set_latest_flow_detail_id] = useState(selectedReturnHeader?.flow_detail_id ?? "RETURN_DRAFT");

  // tipe return
  const [return_type, set_return_type] = useState(selectedReturnHeader?.return_type ?? "BY_PRODUCT"); // "BY_PO" | "BY_PRODUCT"
  const [purchase_header_id, set_purchase_header_id] = useState(selectedReturnHeader?.purchase_header_id ?? null);
  const [purchase_transaction_id, set_purchase_transaction_id] = useState(selectedReturnHeader?.purchase_transaction_id ?? null);

  // warehouse & supplier
  const [warehouse_id, set_warehouse_id] = useState(selectedReturnHeader?.warehouse_id ?? currentBranch?.id ?? null);
  const [warehouse_name, set_warehouse_name] = useState(selectedReturnHeader?.warehouse_name ?? currentBranch?.name ?? null);
  const [warehouse_address, set_warehouse_address] = useState(selectedReturnHeader?.warehouse_address ?? currentBranch?.address ?? null);

  const [supplier_id, set_supplier_id] = useState(selectedReturnHeader?.supplier_id ?? null);
  const [supplier_name, set_supplier_name] = useState(selectedReturnHeader?.supplier_name ?? null);
  const [supplier_phone_number, set_supplier_phone_number] = useState(selectedReturnHeader?.supplier_phone_number ?? null);
  const [supplier_email_address, set_supplier_email_address] = useState(selectedReturnHeader?.supplier_email_address ?? null);
  const [supplier_address, set_supplier_address] = useState(selectedReturnHeader?.supplier_address ?? null);

  const [notes, set_notes] = useState(selectedReturnHeader?.notes ?? null);
  const [ppn_percentage, set_ppn_percentage] = useState(selectedReturnHeader?.ppn_percentage ?? 11);

  // aggregate amount (opsional—kalau kamu punya sisi server menghitung)
  const [rtn_amount, set_rtn_amount] = useState(data?.return_header_side_by_side?.rtn_amount ?? null);
  const [rtn_nett_amount, set_rtn_nett_amount] = useState(data?.return_header_side_by_side?.rtn_nett_amount ?? null);

  // flags role
  const is_admin = getIsAdmin();
  const is_supervisor = getIsSupervisor();
  const is_owner = getIsOwner();

  // --- FETCH PO HEADERS (for BY_PO) ---
    const shouldFetchPO = return_type === "BY_PO";

    // const { data: poListRes, isLoading: isLoadingPOs } = useResultSet(
    // `${process.env.REACT_APP_REMAX_PS_API_URL}`,
    // shouldFetchPO ? `purchase_header` : null,
    // {
    //     supplier_id,                 // optional
    //     flow_detail_in: "SALES_ORDER_COMPLETED",
    //     limit: 50
    // }
    // );

    // const poHeaders = Array.isArray(poListRes?.purchase_header)
    // ? poListRes.purchase_header
    // : Array.isArray(poListRes) ? poListRes : [];

    // const poSelection = poHeaders.map(x => ({
    // id: x.id,
    // name: `${x.id} • ${x.supplier_name ?? "-"}`
    // }));

    // --- FETCH PO ITEMS (for BY_PO) ---
    const { data: poItemsRes } = useResultSet(
    `${process.env.REACT_APP_REMAX_PS_API_URL}`,
    purchase_header_id ? 'purchase_item' : null,
    { parent_id: purchase_header_id } // <-- pass params, don't append to the method string
    );

    // accept multiple shapes from backend
    const poItems = Array.isArray(poItemsRes?.purchase_item_side_by_side)
    ? poItemsRes.purchase_item_side_by_side
    : Array.isArray(poItemsRes?.purchase_item)
    ? poItemsRes.purchase_item
    : Array.isArray(poItemsRes?.data)
    ? poItemsRes.data
    : Array.isArray(poItemsRes)
    ? poItemsRes
    : [];

    const {
        data: returnItemsRes,
        isLoading: isLoadingReturnItems,
        mutate: reloadReturnItems,
    } = useResultSet(
    `${process.env.REACT_APP_REMAX_PS_API_URL}`,
    `return_item?return_header_id=`+id,
    { return_header_id: id }           // kunci: id header
    )

    // Normalisasi bentuk data dari API
    const returnItems = Array.isArray(returnItemsRes?.return_item)
    ? returnItemsRes.return_item
    : Array.isArray(returnItemsRes)
    ? returnItemsRes
    : [];

    // rows used by the table
    const rowsForTable = return_type === 'BY_PO' ? poItems : returnItems;

  // =========================================================
  // HELPER SETTER SERENTAK (biar sinkron di header)
  // =========================================================
  const set_notes_all = (value) => {
    set_notes(value);
    if (selectedReturnHeader) selectedReturnHeader.notes = value;
  };

  const set_warehouse_name_all = (value) => {
    set_warehouse_name(value);
    if (selectedReturnHeader) selectedReturnHeader.warehouse_name = value;
  };

  const set_warehouse_address_all = (value) => {
    set_warehouse_address(value);
    if (selectedReturnHeader) selectedReturnHeader.warehouse_address = value;
  };

  // bila user memilih BY_PO, otomatis isi supplier & warehouse dari PO yang dipilih (kalau data PO ada)
  useEffect(() => {
    if (return_type === "BY_PO" && purchase_header_id && data?.purchase_header?.length > 0) {
      const ph = data.purchase_header.find((h) => h.id === purchase_header_id);
      if (ph) {
        set_supplier_id(ph.supplier_id ?? supplier_id);
        set_supplier_name(ph.supplier_name ?? supplier_name);
        set_supplier_phone_number(ph.supplier_phone_number ?? supplier_phone_number);
        set_supplier_email_address(ph.supplier_email_address ?? supplier_email_address);
        set_supplier_address(ph.supplier_address ?? supplier_address);

        set_warehouse_id(ph.warehouse_id ?? warehouse_id);
        set_warehouse_name(ph.warehouse_name ?? warehouse_name);
        set_warehouse_address(ph.warehouse_address ?? warehouse_address);

        if (!ppn_percentage && ph.ppn_percentage) set_ppn_percentage(ph.ppn_percentage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [return_type, purchase_header_id]);

  // =========================================================
  // PAYLOAD UTAMA
  // =========================================================
  const arr_return = {
    id,
    origin,
    outlet_group_id : outlet_id,
    outlet_group_name,
    outlet_group_phone_number,
    user_id,
    user_name,
    outlet_id,
    outlet_name,
    outlet_phone_number,
    outlet_email_address,
    entry_date,
    purchase_header_id: return_type === "BY_PO" ? purchase_header_id : null,

    warehouse_id,
    warehouse_name,
    warehouse_address,

    supplier_id,
    supplier_name,
    supplier_phone_number,
    supplier_email_address,
    supplier_address,

    notes,
    ppn_percentage,
    return_type,
  };

  // =========================================================
  // AKSI
  // =========================================================
  const handleSubmit = () => {
    if (nProgress.isStarted()) return toast.error("Previous action is loading");
    // console.log(arr_return,"arr_return")
    if(supplier_id == null || warehouse_id == null || outlet_id == null) {
        toast.error("Data Return tidak lengkap")
        return
    }
    onSubmit({
      ...arr_return,
      flow_detail_id,
      latest_flow_detail_id,
      purchase_transaction_id:purchase_transaction_id,
      message: "Return disimpan",
    });
  };

  const sendToReviewer = () => {
    onSubmit({
      ...arr_return,
      flow_detail_id: "RETURN_NEED_REVIEW",
      latest_flow_detail_id: "RETURN_NEED_REVIEW",
      message: "Return dikirim ke reviewer",
    });
  };

  const rejectReturn = () => {
    onSubmit({
      ...arr_return,
      flow_detail_id: "RETURN_REJECTED",
      latest_flow_detail_id: "RETURN_REJECTED",
      message: "Return ditolak",
    });
  };

  const approveReturn = () => {
    onSubmit({
      ...arr_return,
      flow_detail_id: "RETURN_APPROVED",
      latest_flow_detail_id: "RETURN_APPROVED",
      message: "Return disetujui",
    });
  };

  const completeReturn = () => {
    onSubmit({
      ...arr_return,
      flow_detail_id: "RETURN_COMPLETE",
      latest_flow_detail_id: "RETURN_COMPLETE",
      purchase_transaction_id:purchase_transaction_id,
      message: "Return berhasil",
    });
  };

  const closeReturn = () => {
    console.log(purchase_transaction_id,'purchase_transaction_id')
    onSubmit({
      ...arr_return,
      rowsForTable,
      flow_detail_id: "RETURN_CLOSED",
      latest_flow_detail_id: "RETURN_CLOSED",
      purchase_transaction_id:purchase_transaction_id,
      message: "Return ditutup",
    });
  };

  // Print Return (opsional, sama pola dengan print PO; backend kamu sediakan endpoint PDF)
  const printReturnViaAPI = async () => {
    try {
      if (!rowsForTable?.length) {
        toast.error("Tidak ada item Return untuk dicetak.");
        return;
      }
      nProgress.start();

      const headerPayload = {
        id,
        origin,
        date: entry_date,
        supplier: {
          id: supplier_id,
          name: supplier_name,
          phone: supplier_phone_number,
          email: supplier_email_address,
          address: supplier_address,
        },
        ship_from: {
          warehouse_id,
          warehouse_name,
          warehouse_address,
        },
        outlet: {
          id: outlet_id,
          name: outlet_name,
          group_name: outlet_group_name,
        },
        ppn_percentage,
        notes: notes ?? "",
        return_type,
        purchase_header_id: return_type === "BY_PO" ? purchase_header_id : null,
      };

      const itemsPayload = (data?.return_item_side_by_side ?? rowsForTable ?? []).map((r, i) => ({
        no: i + 1,
        product_unit: r.product_unit,
        qty: r.rtn_quantity ?? r.quantity ?? 0,
        price: r.rtn_price ?? r.price ?? 0,
        total_amount: r.rtn_total_nett ?? r.total_amount ?? 0,
        remark: r.notes ?? "",
      }));

      const payload = {
        return_header: headerPayload,
        return_items: itemsPayload,
        meta: {
          printed_by: user?.name ?? "System",
          currency: "IDR",
          outlet_phone: outlet_phone_number ?? null,
          outlet_email: outlet_email_address ?? null,
        },
      };

      const res = await fetch(`${process.env.REACT_APP_INVENTORY_API_URL}/purchase-return/print`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Print gagal (${res.status}) ${text}`);
      }

      const blob = await res.blob();
      const ct = res.headers.get("Content-Type") || "";
      if (!ct.toLowerCase().includes("pdf")) {
        const text = await blob.text();
        throw new Error(text || "Server tidak mengembalikan PDF");
      }

      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      toast.success("Return siap dicetak.");
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Gagal mencetak Return.");
    } finally {
      nProgress.done();
    }
  };

  // =========================================================
  // RENDER
  // =========================================================
  return (
    <div className="mt-8 space-y-8">
      <div className="mt-10 sm:mt-0">
        <div className="space-y-8">
          <div className="md:col-span-1">
            <div className="px-4 sm:px-0">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                {`Return Form ${return_type === "BY_PO" ? "(BY_PO)" : "(BY_PRODUCT)"}`}
              </h3>
              <p className="mt-1 text-sm text-gray-600">{lang.please_fill_the_provided_form_input}</p>
            </div>
          </div>

          {/* HEADER */}
          <div className="mt-5 md:col-span-2 md:mt-0">
            <div className="border sm:rounded-xl">
              <div className="px-4 py-5 sm:p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="id" className="block text-xs text-neutral-700">
                      {`Return Number`}<sup className="text-xs text-red-700"> *</sup>
                    </label>
                    <Input placeholder="Generated by system" disabled={true} maxLength={100} value={id} id="id" error={errors?.id} />
                  </div>
                  <div>
                    <label htmlFor="latest_flow_detail_id" className="block text-xs text-neutral-700">
                      {lang.status}<sup className="text-xs text-red-700"> *</sup>
                    </label>
                    <Input placeholder="" disabled={true} maxLength={100} value={latest_flow_detail_id} id="latest_flow_detail_id" error={errors?.latest_flow_detail_id} />
                  </div>

                  <div>
                    <label className="block text-xs text-neutral-700">Return Type<sup className="text-xs text-red-700"> *</sup></label>
                    <SelectSearchable
                      disabled={latest_flow_detail_id == "RETURN_CLOSED" ? true : false}
                      selection={[
                        { id: "BY_PRODUCT", name: "BY_PRODUCT (tanpa PO)" },
                        { id: "BY_PO", name: "BY_PO (pilih PO)" },
                      ]}
                      value={return_type}
                      keyValue={(v) => v.id}
                      title={(v) => v.name}
                      onChange={(val) => set_return_type(val)}
                      placeholder="-- Select Return Type --"
                    />
                    <ErrorMessage error={errors?.return_type} />
                  </div>

                  {return_type === "BY_PO" && (
                    <div>
                        <label className="block text-xs text-neutral-700">
                        Purchase (PO) <sup className="text-xs text-red-700"> *</sup>
                        </label>
                        <AsyncPOHeaderPicker
                        disabled={latest_flow_detail_id == "RETURN_CLOSED" ? true : false}
                        value={purchase_header_id}
                        initialLabel={purchase_header_id ? `${purchase_header_id}` : ""}
                        supplier_id={supplier_id}
                        warehouse_id={warehouse_id}
                        outlet_id={outlet_id}
                        onChange={(poId, ph) => {
                            set_purchase_header_id(poId);
                            set_purchase_transaction_id(ph?.purchase_transaction_id ?? null);
                            // hydrate header fields from the chosen PO (cheap, no extra fetch)
                            if (ph) {
                            set_supplier_id(ph.supplier_id ?? null);
                            set_supplier_name(ph.supplier_name ?? null);
                            set_supplier_phone_number(ph.supplier_phone_number ?? null);
                            set_supplier_email_address(ph.supplier_email_address ?? null);
                            set_supplier_address(ph.supplier_address ?? null);

                            set_outlet_id(ph.warehouse_id ?? null);
                            set_warehouse_id(ph.warehouse_id ?? null);
                            set_warehouse_name(ph.warehouse_name ?? null);
                            set_warehouse_address(ph.warehouse_address ?? null);

                            if (!ppn_percentage && ph.ppn_percentage) {
                                set_ppn_percentage(ph.ppn_percentage);
                            }
                            }
                        }}
                        />
                        <ErrorMessage error={errors?.purchase_header_id} />
                    </div>
                    )}


                  <div>
                    <label htmlFor="user_name" className="block text-xs text-neutral-700">
                      {lang.user_name}<sup className="text-xs text-red-700"> *</sup>
                    </label>
                    <Input placeholder="User Name" disabled={true} maxLength={100} value={user_name} id="user_name" error={errors?.user_name} />
                  </div>

                  <div>
                    <label htmlFor="outlet_name" className="block text-xs text-neutral-700">
                      {lang.outlet_name}<sup className="text-xs text-red-700"> *</sup>
                    </label>
                    <SelectSearchable
                      disabled={latest_flow_detail_id == "RETURN_CLOSED" ? true : false}
                      filter={(value) => value.name}
                      onChange={(value) => {
                        const tmp = branchSelection?.find((item) => item.id == value);
                        set_outlet_id(value);
                        set_outlet_name(tmp?.name);
                        set_outlet_phone_number(tmp?.contact);
                        set_warehouse_id(tmp?.id);
                        set_warehouse_name(tmp?.name);
                        set_warehouse_address(tmp?.address);
                      }}
                      selection={branchSelection}
                      isLoading={false}
                      value={outlet_id ?? outlet_group_id}
                      keyValue={(v) => v.id}
                      title={(v) => v.name}
                      placeholder={`-- Select Outlet --`}
                    />
                    <ErrorMessage error={errors?.outlet_name} />
                  </div>
                </div>
              </div>
            </div>

            {/* SHIP & SUPPLIER */}
            <div className="my-2 border sm:rounded-xl">
              <div className="px-4 py-5 sm:p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="warehouse_name" className="block text-xs text-neutral-700">
                      {lang.deliver_to}<sup className="text-xs text-red-700"> *</sup>
                    </label>
                    <Input disabled={true} placeholder="Deliver to" maxLength={100} onChange={(e) => set_warehouse_name_all(e.target.value)} value={warehouse_name} id="warehouse_name" error={errors?.warehouse_name} />
                    <ErrorMessage error={errors?.warehouse_name} />
                  </div>
                  <div>
                    <label htmlFor="supplier_name" className="block text-xs text-neutral-700">
                      {lang.supplier_name}<sup className="text-xs text-red-700"> *</sup>
                    </label>
                    <SelectSearchable
                      disabled={latest_flow_detail_id == "RETURN_CLOSED" ? true : false}
                      filter={(value) => value.name}
                      onChange={(value) => {
                        set_supplier_id(value);
                        const tmp = supplierSelection?.find((item) => item.id == value);
                        set_supplier_name(tmp?.name);
                        set_supplier_phone_number(tmp?.mobile ?? tmp?.contact_number ?? tmp?.code);
                        set_supplier_address(tmp?.address);
                      }}
                      selection={supplierSelection}
                      isLoading={false}
                      value={supplier_id}
                      keyValue={(v) => v.id}
                      title={(v) => v.name}
                      placeholder={`-- Select Supplier --`}
                    />
                    <ErrorMessage error={errors?.supplier_name} />
                  </div>

                  <div>
                    <label htmlFor="warehouse_address" className="block text-xs text-neutral-700">
                      {lang.delivery_address}<sup className="text-xs text-red-700"> *</sup>
                    </label>
                    <textarea
                      disabled={true}
                      onChange={(e) => set_warehouse_address_all(e.target.value)}
                      value={warehouse_address}
                      name="warehouse_address"
                      id="warehouse_address"
                      rows="3"
                      className="block w-full px-4 py-2 mt-1 text-sm transition border disabled:bg-neutral-200 border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                    ></textarea>
                    <ErrorMessage error={errors?.warehouse_address} />
                  </div>

                  <div>
                    <label htmlFor="notes" className="block text-xs text-neutral-700">
                      {lang.notes}
                    </label>
                    <textarea
                      disabled={latest_flow_detail_id == "RETURN_CLOSED" ? true : false}
                      onChange={(e) => set_notes_all(e.target.value)}
                      value={notes ?? ""}
                      name="notes"
                      id="notes"
                      rows="3"
                      className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                    ></textarea>
                    <ErrorMessage error={errors?.notes} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* TOMBOL PRINT */}
          {(rowsForTable?.length ?? 0) > 0 && (
            <div className="flex justify-end space-x-2">
              <button type="button" onClick={printReturnViaAPI} className="bg-red-800 inline-flex items-center p-2 text-white text-xs transition rounded active:hover:scale-90">
                <span>Print Return</span>
              </button>
            </div>
          )}

          {/* TOMBOL WORKFLOW */}
          {(is_admin == 1 || is_supervisor == 1 || is_owner == 1) && (
            <div className="flex flex-wrap gap-2 justify-end">
              {/* {(latest_flow_detail_id === "RETURN_DRAFT" || latest_flow_detail_id === "RETURN_REJECTED") && (
                <button type="button" onClick={sendToReviewer} className="bg-red-800 inline-flex items-center p-1 text-white text-xs transition rounded active:hover:scale-90">
                  <span>{`Send to reviewer`}</span>
                </button>
              )}
              {(latest_flow_detail_id === "RETURN_NEED_REVIEW" || latest_flow_detail_id === "RETURN_IN_REVIEW") && is_supervisor == 1 && (
                <>
                  <button type="button" onClick={rejectReturn} className="opacity-90 bg-red-700 inline-flex items-center p-1 text-white text-xs transition rounded active:hover:scale-90">
                    <span>{`Reject`}</span>
                  </button>
                  <button type="button" onClick={approveReturn} className="bg-red-800 inline-flex items-center p-1 text-white text-xs transition rounded active:hover:scale-90">
                    <span>{`Approve Return`}</span>
                  </button>
                </>
              )} */}
              {(rowsForTable?.length ?? 0) > 0 && (latest_flow_detail_id === "RETURN_DRAFT" || latest_flow_detail_id === "RETURN_REJECTED") && (
                <button type="button" onClick={completeReturn} className="bg-red-800 inline-flex items-center p-1 text-white text-xs transition rounded active:hover:scale-90">
                  <span>{`Complete Return`}</span>
                </button>
              )}
              {(latest_flow_detail_id === "RETURN_COMPLETE") && (
                <button type="button" onClick={closeReturn} className="bg-red-800 inline-flex items-center p-1 text-white text-xs transition rounded active:hover:scale-90">
                  <span>{`Close Return`}</span>
                </button>
              )}
            </div>
          )}

          {/* GRID ITEM */}
            <div className="overflow-x-auto border border-neutral-200 rounded-xl">
            {/* Add button for BY_PRODUCT */}
            {!shouldFetchPO &&
                ["RETURN_DRAFT","RETURN_REJECTED","RETURN_NEED_REVIEW","RETURN_IN_REVIEW"].includes(latest_flow_detail_id) &&
                (is_admin == 1 || is_supervisor == 1 || is_owner == 1) && (
                <AddProduct
                    onSubmit={() => {}}
                    productUnitSelection={productUnitOptions}
                    productItems={data?.return_item_side_by_side ?? returnItems ?? []}
                    add={true}
                    purchase={{ ...arr_return, origin: 'RT', flow_detail_id, latest_flow_detail_id }}
                />
            )}

            <table className="min-w-full overflow-x-auto divide-y divide-neutral-200">
                <thead className="bg-neutral-50 rounded-t-3xl">
                <tr>
                    <th className="px-1 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap"></th>
                    <th className="px-1 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Product Unit</th>
                    {return_type === "BY_PO" && (
                    <th className="px-1 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">PO Ref</th>
                    )}
                    <th className="px-1 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">RTN Qty</th>
                    <th className="px-1 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">RTN Price</th>
                    <th className="px-1 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">RTN Total Nett</th>
                    <th className="px-1 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Remark</th>
                </tr>
                </thead>

                <tbody className="bg-white divide-y divide-neutral-200">
                {rowsForTable.length === 0 && !search && (
                    <tr className="text-center">
                    <td colSpan="7" className="px-1 py-4 text-xs text-neutral-500 whitespace-nowrap">
                        {lang.no_data}
                    </td>
                    </tr>
                )}

                {rowsForTable.map((row, idx) => (
                    <tr key={row.product_unit_id ?? idx}>
                    <td className="px-1 py-4 text-xs whitespace-nowrap">
                        {["RETURN_DRAFT","RETURN_REJECTED","RETURN_NEED_REVIEW","RETURN_IN_REVIEW"].includes(latest_flow_detail_id) &&
                        (is_admin == 1 || is_supervisor == 1 || is_owner == 1) && (
                            <div className="inline-flex items-center space-x-2">
                            <DeleteReturnItem
                                onSubmit={onSubmit}
                                item={row}
                                header={{ ...arr_return, flow_detail_id, latest_flow_detail_id }}
                            />
                            </div>
                        )}
                    </td>
                    <td className="px-1 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                        {row.product_unit}
                    </td>
                    {return_type === "BY_PO" && (
                        <td className="px-1 py-4 text-xs text-neutral-500 whitespace-nowrap">
                        {purchase_header_id ?? "-"}
                        </td>
                    )}
                    <td className="px-1 py-4 text-xs text-neutral-500 whitespace-nowrap">
                        {row.rtn_quantity ?? row.quantity ?? "-"}
                    </td>
                    <td className="px-1 py-4 text-xs text-neutral-500 whitespace-nowrap">
                        {row.rtn_price
                        ? Intl.NumberFormat("id-Id", { style: "currency", currency: "idr" }).format(row.rtn_price)
                        : row.price
                        ? Intl.NumberFormat("id-Id", { style: "currency", currency: "idr" }).format(row.price)
                        : "-"
                        }
                    </td>
                    <td className="px-1 py-4 text-xs text-neutral-500 whitespace-nowrap">
                        {row.rtn_total_nett
                        ? Intl.NumberFormat("id-Id", { style: "currency", currency: "idr" }).format(row.rtn_total_nett)
                        : row.total_amount
                        ? Intl.NumberFormat("id-Id", { style: "currency", currency: "idr" }).format(row.total_amount)
                        : "-"
                        }
                    </td>
                    <td className="px-1 py-4 text-xs text-neutral-500 whitespace-nowrap">
                        {row.notes ?? "-"}
                    </td>
                    </tr>
                ))}
                </tbody>
            </table>
            </div>


          {/* TOMBOL SAVE */}
          {["RETURN_DRAFT", "RETURN_REJECTED", "RETURN_NEED_REVIEW", "RETURN_IN_REVIEW"].includes(latest_flow_detail_id) &&
            (is_admin == 1 || is_supervisor == 1 || is_owner == 1) && (
              <div className="mt-8 text-xs text-right">
                <button onClick={handleSubmit} className="items-center px-6 py-3 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90">
                  <span>{`${lang.save}`}</span>
                </button>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

function DeleteReturnItem({ onSubmit, item, header }) {
  const handleDelete = () => {
    if (!item?.product_unit_id) return toast.error("Item tidak valid");
    onSubmit({
      ...header,
      action: "DELETE_RETURN_ITEM",
      arr_return_item: JSON.stringify([
        {
          product_unit_id: item.product_unit_id,
        },
      ]),
      message: "Hapus item return",
    });
  };

  return (
    <button
      type="button"
      onClick={handleDelete}
      className="bg-red-600 inline-flex items-center px-2 py-1 text-white text-[11px] transition rounded active:hover:scale-90"
      title="Delete item"
    >
      Hapus
    </button>
  );
}
